import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Clock, ChatTeardropDots, EnvelopeOpen } from "@phosphor-icons/react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ContactForm } from "@/components/kontakt/ContactForm";
import { ssrPage } from "@/ssr/define-page";
import { definePageMeta } from "@/ssr/define-meta";
import { buildPageStructuredData } from "@/ssr/structured-data";

const SALES_EMAIL = "info@psihotron.ba";

const kontaktFirmeMeta = definePageMeta({
  titleKey: "contact.meta.title",
  descriptionKey: "contact.meta.description",
  ogImage: "/og-contact.jpg",
  structuredData: (ctx) =>
    buildPageStructuredData(ctx, {
      titleKey: "contact.meta.title",
      descriptionKey: "contact.meta.description",
      ogImage: "/og-contact.jpg",
      pageType: "ContactPage",
      breadcrumbs: [
        { name: "Psihotron", path: "/" },
        { name: ctx.t("nav.forCompanies"), path: "/za-firme" },
        { name: ctx.t("nav.contact"), path: "/kontakt-firme" },
      ],
    }),
});

export const Route = createFileRoute("/kontakt-firme")(
  ssrPage({ component: KontaktFirmePage, meta: kontaktFirmeMeta }),
);

function KontaktFirmePage() {
  const ref = useScrollReveal<HTMLElement>();
  const { t } = useTranslation();

  return (
    <section
      ref={ref}
      className="animate-on-scroll relative isolate overflow-hidden pt-28 pb-24 sm:pt-32 sm:pb-28"
      style={{
        background:
          "linear-gradient(180deg, var(--color-section-bg) 0%, oklch(98% 0.012 84) 60%, oklch(97% 0.018 84) 100%)",
      }}
    >
      {/* hairline grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, var(--color-warm-border) 1px, transparent 1px)",
          backgroundSize: "calc(100% / 12) 100%",
        }}
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-12 gap-y-14 px-4 sm:px-6 lg:gap-x-8 lg:px-8">
        {/* Left rail — context */}
        <div className="col-span-12 lg:col-span-5">
          {/* Heading */}
          <h1 className="font-heading text-4xl leading-[1.05] font-bold tracking-tight text-foreground sm:text-5xl lg:text-[4.25rem]">
            {t("kontakt.hero.title")}{" "}
            <span
              className="relative inline-block italic"
              style={{ color: "var(--color-brand-dark)" }}
            >
              {t("kontakt.hero.titleHighlight")}
              <span
                aria-hidden
                className="absolute -bottom-1 left-0 h-[3px] w-full origin-left"
                style={{
                  background:
                    "linear-gradient(90deg, var(--color-brand) 0%, transparent 100%)",
                }}
              />
            </span>
            <span className="text-foreground">
              {t("kontakt.hero.titleAfter")}
            </span>
          </h1>

          {/* Sub */}
          <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t("kontakt.hero.subtitle")}
          </p>

          {/* Direct email card */}
          <a
            href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent(
              "Psihotron — direktni upit",
            )}`}
            className="mt-9 flex items-center justify-between gap-4 rounded-xl bg-white px-5 py-4 transition-shadow hover:shadow-md"
            style={{
              border: "1px solid var(--color-warm-border)",
              boxShadow: "0 1px 0 oklch(96% 0.01 84)",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex size-10 items-center justify-center rounded-full"
                style={{
                  background: "var(--color-brand-bg-light)",
                  color: "var(--color-brand-dark)",
                }}
              >
                <EnvelopeOpen weight="duotone" className="size-5" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-[10px] font-semibold tracking-[0.2em] uppercase"
                  style={{ color: "var(--color-section-label)" }}
                >
                  {t("kontakt.hero.directLine")}
                </div>
                <div className="font-medium text-foreground">{SALES_EMAIL}</div>
              </div>
            </div>
          </a>

          {/* Trust strip */}
          <ul className="mt-8 space-y-4">
            <TrustItem
              icon={<Clock weight="duotone" className="size-4" />}
              text={t("kontakt.hero.responseTime")}
            />
            <TrustItem
              icon={<ChatTeardropDots weight="duotone" className="size-4" />}
              text={t("kontakt.hero.noBots")}
            />
          </ul>
        </div>

        {/* Right rail — form */}
        <div className="relative col-span-12 lg:col-span-7">
          {/* faint vertical rule on lg+ */}
          <div
            aria-hidden
            className="absolute -left-4 top-0 hidden h-full w-px lg:block"
            style={{ background: "var(--color-warm-border)" }}
          />
          <ContactForm />
        </div>
      </div>
    </section>
  );
}

function TrustItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <li className="flex items-center gap-3">
      <span
        className="flex size-7 items-center justify-center rounded-full"
        style={{
          background: "var(--color-brand-bg-light)",
          color: "var(--color-brand-dark)",
        }}
      >
        {icon}
      </span>
      <span className="text-sm text-muted-foreground">{text}</span>
    </li>
  );
}
